import { useState, useCallback, useEffect } from 'react';
import { navigate } from 'gatsby';
import { API } from 'aws-amplify';
import debounce from 'lodash/debounce';
import { postMessageToParent, formSubmissionNotifier } from 'utils/iframeUtils';
import queryString from 'query-string';
import { getPathname } from 'utils/location';
import { ORIGIN, MINIMAL_PHONE_LENGTH } from '../const/constants';
import { parseMarketingConsent } from './parsers';
import { formatPhoneNumber } from './formatters';
import { parseUtmToApiSchema } from './utmParams';
import { UtmEvent, UtmOutput } from '../models';
import { emitDatalayerEvent } from './gtmDatalayer';

const controlForm = (initialSchema, props) => {
  const { language } = props;
  const [utm, setUtm] = useState<UtmOutput | null>(null);
  const [fields, setFields] = useState<any>(initialSchema);
  const [isLoading, setLoading] = useState<boolean>(false);
  const [state, setState] = useState<any>({
    website: '',
    phoneCode: '',
    idMarket: '',
    idBrand: '',
    languageCode: '',
  });
  const postMessageToParentDebounced = useCallback(debounce(postMessageToParent, 100), []);
  const postMessageToParentFormSubmitted = useCallback(() => formSubmissionNotifier(), []);

  const handleParentUtmParams = (event: MessageEvent<UtmEvent>) => {
    if (event.data?.type !== 'utm') {
      return;
    }

    const { data } = event.data;
    setUtm(parseUtmToApiSchema(data));
  };

  useEffect(() => {
    const queriedTheme = queryString.parse(window.location.search);

    setState({
      website: queriedTheme?.site || '',
      phoneCode: queriedTheme?.phoneCode || 'gb',
      idMarket: queriedTheme?.idMarket || '',
      idBrand: queriedTheme?.idBrand || '',
      languageCode: queriedTheme?.languageCode || 'en_GB',
    });
  }, []);

  useEffect(() => {
    window.addEventListener('message', handleParentUtmParams);

    return () => {
      window.removeEventListener('message', handleParentUtmParams);
    };
  }, []);

  const validateField = (field, mailParameter?, phoneData?) => {
    let message = null;

    const fieldHasValidator = field?.validators?.length;
    if (fieldHasValidator) {
      field.validators.forEach((v) => {
        message = !v.isValid(field.value, mailParameter || phoneData) ? v.message : message;
      });
    }

    field.errorMessage = message;
    const isEmailField = field.id === 'email';
    if (isEmailField && !field.errorMessage) {
      const mailField = fields.find((item) => item.id === 'email');

      if (mailField.value) {
        mailField.errorMessage = null;
      }
    }

    postMessageToParentDebounced();

    return field.errorMessage;
  };

  const handleFieldChange = (event, phoneProps) => {
    const mailField = fields.find((field) => field.id === 'email');

    fields.forEach((field) => {
      const isCurrentField = field.name === (event?.target?.name || 'phone');

      if (isCurrentField) {
        const inputValue =
          field.type === 'checkbox'
            ? event.target.checked
            : event?.target
            ? event.target.value
            : event;

        field.value = inputValue;

        if (field.name === 'phone') field.phoneFormat = { dialCode: phoneProps.dialCode };

        validateField(
          field,
          field.id === 'email' ? mailField?.value : null,
          field.id === 'phoneNumber' ? { dialCode: phoneProps.dialCode } : null
        );
      }
    });
    setFields([...fields]);
  };

  const getRequestBody = (utmParams: UtmOutput | null) => {
    const formData = initialSchema.reduce(
      (acc, { name, value }) => ({ ...acc, [name]: value }),
      {}
    );
    const {
      firstName,
      lastName,
      email,
      caseType,
      orderNumber,
      description,
      phone,
      marketingConsent,
    } = formData;
    const marketConsent = parseMarketingConsent(marketingConsent);
    const { website, idBrand, idMarket, languageCode } = state;
    const parsedEmail = email.toLowerCase();

    return {
      id: props.location.pathname,
      language: languageCode || language,
      ...(firstName && { firstName }),
      lastName,
      ...(email && { email: parsedEmail }),
      caseType,
      ...(orderNumber && { orderNumber }),
      description,
      ...(phone && phone.length > MINIMAL_PHONE_LENGTH && { phone: formatPhoneNumber(phone) }),
      ...(marketConsent && { marketConsent }),
      ...(website && { website }),
      ...(idBrand && { brand: idBrand }),
      ...(idMarket && { market: idMarket }),
      ...(utmParams && { ...utmParams }),
      origin: ORIGIN,
    };
  };

  const sendSubmitData = async (captchaToken: string) => {
    setLoading(true);
    const body = getRequestBody(utm);

    try {
      const { status, caseNumber } = await API.post(
        'api',
        `/contactForm?g-recaptcha-response=${captchaToken}`,
        {
          body,
        }
      );

      if (status === 'success') {
        navigate(`${getPathname()}thank-you?status=${status}`, {
          state: {
            status,
            caseNumber,
          },
        });

        emitDatalayerEvent();
        postMessageToParentFormSubmitted();

        // clear all fields after success submit
        const copiedFields = [...fields];
        copiedFields.forEach((field) => {
          field.value = '';
        });
        setFields(copiedFields);
      }
    } catch (error: any) {
      navigate(`${getPathname()}thank-you`);
      console.error(error.response?.data);
    }
    setLoading(false);
  };

  const handleSubmit = (captchaToken) => {
    const mailField = fields.find((field) => field.id === 'email');

    fields
      .filter((item) => {
        if (!item.visibilityCondition) return true;
        const comparer = fields.find(({ id }) => item.visibilityCondition?.field === id);

        return comparer.value === item.visibilityCondition.value;
      })
      .forEach((field) => {
        validateField(
          field,
          field.id === 'confirmEmail'
            ? mailField?.value
            : field.id === 'phoneNumber'
            ? field.phoneFormat
            : null
        );
      });

    const isFoundError = fields.find((elem) => elem.errorMessage);

    if (isFoundError?.id) {
      setFields([...fields]);
      const el =
        isFoundError.id === 'phoneNumber'
          ? document.getElementsByClassName('phone-input')[0]
          : document.getElementById(isFoundError.id);
      (el as HTMLElement)?.focus();
    } else sendSubmitData(captchaToken);
  };

  return [fields, handleFieldChange, handleSubmit, isLoading, state.phoneCode];
};

export default controlForm;
